import { Box, Container, Grid, Skeleton, Stack, styled, Tooltip, Typography, useTheme } from '@mui/material';
import { filter, find, get, isEmpty, map, size } from 'lodash';

import React, { ChangeEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';

// Components
import FlagCountry from 'src/components/common/FlagCountry';
import Point from 'src/components/common/Point';
import NoDataSearchCompany from 'src/features/CompanyProfiles/NoDataSearchCompany';
import StyledPagination from 'src/components/common/StyledPagination';
import { useQueryParams } from 'src/hooks/useQueryParam';
import { LIMIT_GET_LIST } from 'src/utils/constants/config';
import { getCountryByCountryCode, renderTextOrDefault } from 'src/utils/helpers/stringHelpers';
import { useQueryListTradeDataHSCode } from 'src/services/queries/useQueryTradeData';
import FilterTradeDataProductList from 'src/pages/TradeDataProductList/Filter';
import ProductItemDetail from 'src/features/TradeData/ProductList/ProductItemDetail';
import ProductItemDefaultImg from 'src/asset/images/trade-data/ProductItemDefault.png';

const StyledBox = styled(Box)(({ theme }) => ({
  border: `2px solid ${theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius * 2,
  '&:hover': {
    borderColor: theme.palette.primary.main,
  },
  position: 'relative',
  background: theme.palette.background.default,
}));

const TradeDataProductList = () => {
  const query = useQueryParams();

  const [, setSearchParams] = useSearchParams();
  const { data, isFetching } = useQueryListTradeDataHSCode(
    {
      country_code: query.imp_country_code,
      keyword: query.hs_code_keyword,
      offset: query.search_product_offset,
    },
  );
  const { data: listProducts = [] } = data;
  const [selected, setSelected] = useState(undefined as undefined | ITradeDataProductItem);
  const [selectedCompanies, setSelectedCompanies] = useState([] as ITradeDataProductItem[]);
  const theme = useTheme();
  const { palette } = theme;

  const renderLoading = () => (
    <Grid container spacing={2}>
      {Array(Number(query?.limit || LIMIT_GET_LIST))
        .fill('')
        .map((_, index) => (
          <Grid key={`skeleton-profile-company-${index}`} sm={12} md={12} lg={12} xl={6} item>
            <SkeletonProfile />
          </Grid>
        ))}
    </Grid>
  );

  return (
    <Container
      maxWidth="xl"
      sx={{
        margin: '0px auto',
        py: 2,
      }}
    >
      <Stack gap={3}>
        <Stack direction={'row'} flexWrap="wrap" justifyContent={'space-between'}>
          <Typography variant="h5">Trade Data Search Result</Typography>
        </Stack>

        <Box>
          <FilterTradeDataProductList />
        </Box>

        {isFetching ? (
          renderLoading()
        ) : (
          <React.Fragment>
            {listProducts.length === 0 ? (
              <NoDataSearchCompany />
            ) : (
              <Grid spacing={3} container>
                <Grid sm={12} md={8} lg={8} xl={9} item>
                  <Grid container spacing={2}>
                    {listProducts.map((product: ITradeDataProductItem) => {
                      const isChecked = find(selectedCompanies, (o) => o.id === product.id) ? true : false;
                      return (
                        <Grid sm={12} md={12} lg={12} xl={6} key={product.id} item>
                          <Profile
                            isChecked={isChecked}
                            onSelect={() => {
                              setSelected(product);
                            }}
                            selected={selected}
                            product={product}
                          />
                        </Grid>
                      );
                    })}
                  </Grid>
                </Grid>
                {selected && (
                  <Grid sm={12} md={4} lg={4} xl={3} item>
                    <ProductItemDetail selected={selected} />
                  </Grid>
                )}
              </Grid>
            )}
          </React.Fragment>
        )}
        <Grid spacing={3} container>
          {listProducts.length > 0 && (
            <Grid sm={12} md={8} lg={8} xl={9} item>
              <StyledPagination
                total_pages={data.total_pages}
                page={Number(query.search_product_offset) + 1}
                onChange={function (_: ChangeEvent<unknown>, value: number): void {
                  setSearchParams((searchParams) => {
                    searchParams.set('search_product_offset', String(value - 1));
                    return searchParams;
                  });
                }}
              />
            </Grid>
          )}
        </Grid>
      </Stack>
    </Container>
  );
};

export default TradeDataProductList;

const Profile = ({
  product,
  selected,
  onSelect,
  isChecked,
}: {
  product: ITradeDataProductItem;
  selected?: ITradeDataProductItem;
  onSelect: () => void;
  isChecked: boolean;
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isSelected = selected?.code === product.code ? true : false;
  const country = getCountryByCountryCode(product?.country_code || '');

  const renderCountryInfo = () => {
    if (!country.code) return null;

    return (
      <>
        <FlagCountry code={country.code} />

        <Typography variant="caption" color={'text.secondary'}>
          ({country.code})
        </Typography>

        <Point />
      </>
    );
  };

  return (
    <StyledBox
      onClick={onSelect}
      sx={{
        borderColor: isSelected ? theme.palette.primary.main : theme.palette.divider,
        cursor: 'pointer',
      }}
      display={'flex'}
      flexDirection="column"
      gap={1}
      padding={1.5}
      height="100%"
      position={'relative'}
    >
      <Box component={'img'} width={100} height={100} src={ProductItemDefaultImg} />
      <Tooltip placement="left-start" title={`${product.code} - ${product.description}`}>
        <Typography noWrap pr={1} color={'primary.main'} variant="subtitle1">
          {renderTextOrDefault(`${product.code} - ${product.description}`)}
        </Typography>
      </Tooltip>
    </StyledBox>
  );
};

const SkeletonProfile = () => {
  return (
    <StyledBox display={'flex'} flexDirection="column" gap={1} padding={1.5}>
      <Skeleton animation="wave" />
      <Skeleton animation="wave" variant="rectangular" height={24} width={60} />
      <Skeleton animation="wave" variant="rectangular" height={24} width={60} />
      <Skeleton animation="wave" variant="rectangular" height={24} width={60} />
    </StyledBox>
  );
};
